import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { log, warn } from '../utils/logger';

const isBrowser = typeof window !== 'undefined';
const AUTH_TOKEN_KEYS = ['authToken', 'token'];
const USER_STORAGE_KEYS = ['userData', 'user'];

const safeGetItem = (key) => {
  if (!isBrowser) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    warn('[AuthContext] Unable to access localStorage key', key, error);
    return null;
  }
};

const safeSetItem = (key, value) => {
  if (!isBrowser) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    warn('[AuthContext] Unable to persist localStorage key', key, error);
  }
};

const normalizePermissions = (permissions) => {
  if (!permissions) return [];

  try {
    if (typeof permissions === 'string') {
      const parsed = JSON.parse(permissions);
      if (Array.isArray(parsed)) {
        permissions = parsed;
      }
    }
  } catch {
    // ignore parse errors, fall back to original value
  }

  const list = Array.isArray(permissions) ? permissions : [];
  const normalized = list
    .map((permission) => {
      if (typeof permission === 'string') return permission.trim();
      if (permission && typeof permission === 'object') {
        return (permission.name || permission.nombre || '').trim();
      }
      return null;
    })
    .filter(Boolean);
  
  // Debug logging (solo en desarrollo)
  if (process.env.NODE_ENV === 'development' && normalized.length > 0) {
    log('[AuthContext] Permisos normalizados:', normalized);
  }
  
  return normalized;
};

const normalizeUser = (rawUser) => {
  if (!rawUser) {
    return null;
  }

  let userObject = rawUser;
  if (typeof rawUser === 'string') {
    try {
      userObject = JSON.parse(rawUser);
    } catch (error) {
      warn('[AuthContext] Error parsing user data', error);
      return null;
    }
  }

  if (!userObject || typeof userObject !== 'object') {
    return null;
  }

  const firstName = userObject.name || userObject.nombre || '';
  const lastName = userObject.apellido || '';
  const fullName = `${firstName} ${lastName}`.trim() || 'Usuario';

  // Obtener permisos (el servidor envía 'permisos' en español, pero también puede venir 'permissions' en inglés)
  const rawPermissions = userObject.permisos || userObject.permissions;
  const normalizedPermissions = normalizePermissions(rawPermissions);
  
  // Obtener IDs de permisos (el servidor envía 'permisosIds' en español, pero también puede venir 'permissionIds' en inglés)
  const rawPermissionIds = userObject.permisosIds || userObject.permissionIds || [];
  const normalizedPermissionIds = Array.isArray(rawPermissionIds) 
    ? rawPermissionIds.map(id => typeof id === 'number' ? id : parseInt(id, 10)).filter(id => !isNaN(id))
    : [];
  
  // Obtener roles (el servidor puede enviar 'roles' como array)
  const rawRoles = userObject.roles || [];
  const normalizedRoles = Array.isArray(rawRoles) ? rawRoles : [];

  // Debug logging (solo en desarrollo)
  if (process.env.NODE_ENV === 'development' && rawPermissions) {
    log('[AuthContext] Normalizando usuario:', {
      rawPermissions,
      normalizedPermissions,
      rawPermissionIds,
      normalizedPermissionIds,
      hasPanelAdmin: normalizedPermissions.some(p => {
        const pStr = String(p || '').trim().toLowerCase().replace(/\s+/g, '');
        return pStr === 'paneladmin';
      }),
      hasPanelAdminId: normalizedPermissionIds.includes(741258), // ID correcto según init-roles-permissions.js
    });
  }

  return {
    id: userObject.id || userObject._id || userObject.userId || null,
    name: fullName,
    email: userObject.email || userObject.correo || '',
    role: userObject.rol || userObject.role || 'Usuario', // Priorizar 'rol' (español) del backend
    rolId: userObject.rolId || userObject.roleId || null, // ID del rol para compatibilidad
    permissions: normalizedPermissions,
    permissionIds: normalizedPermissionIds, // IDs de permisos para verificación segura
    roles: normalizedRoles, // Array de roles (many-to-many)
    raw: userObject,
  };
};

const readAuthState = () => {
  if (!isBrowser) {
    return {
      token: null,
      user: null,
      isAuthenticated: false,
      permissions: [],
      permissionIds: [],
    };
  }

  const token =
    AUTH_TOKEN_KEYS.map((key) => safeGetItem(key)).find((value) => !!value) || null;

  const rawUser =
    USER_STORAGE_KEYS.map((key) => safeGetItem(key)).find((value) => !!value) || null;

  const user = normalizeUser(rawUser);

  return {
    token,
    user,
    isAuthenticated: Boolean(token && user),
    permissions: user?.permissions || [],
    permissionIds: user?.permissionIds || [],
  };
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(() => readAuthState());

  const refresh = useCallback(() => {
    setAuthState(readAuthState());
  }, []);

  const clearStoredCredentials = useCallback(() => {
    if (!isBrowser) return;
    [...AUTH_TOKEN_KEYS, ...USER_STORAGE_KEYS].forEach((key) => {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        warn('[AuthContext] Unable to clear localStorage key', key, error);
      }
    });
  }, []);

  const setSession = useCallback(
    (session) => {
      if (!session || !session.token || !session.user) {
        clearStoredCredentials();
        setAuthState({
          token: null,
          user: null,
          isAuthenticated: false,
          permissions: [],
          permissionIds: [],
        });
        return;
      }

      const normalizedUser = normalizeUser(session.user);
      if (!normalizedUser) {
        warn('[AuthContext] Unable to normalize user payload during setSession');
        return;
      }

      if (isBrowser) {
        safeSetItem('token', session.token);
        safeSetItem('authToken', session.token);
        safeSetItem('user', JSON.stringify(session.user));
        safeSetItem('userData', JSON.stringify(session.user));
      }

      setAuthState({
        token: session.token,
        user: normalizedUser,
        isAuthenticated: true,
        permissions: normalizedUser.permissions || [],
        permissionIds: normalizedUser.permissionIds || [],
      });
    },
    [clearStoredCredentials],
  );

  const logout = useCallback(
    (options = {}) => {
      clearStoredCredentials();
      setAuthState({
        token: null,
        user: null,
        isAuthenticated: false,
        permissions: [],
      });

      if (isBrowser) {
        window.dispatchEvent(new CustomEvent('userLoggedOut'));
        const redirectTarget = options.redirectTo || '/';
        if (options.shouldRedirect !== false) {
          window.location.assign(redirectTarget);
        }
      }
    },
    [clearStoredCredentials],
  );

  useEffect(() => {
    if (!isBrowser) return undefined;

    const relevantKeys = new Set([...AUTH_TOKEN_KEYS, ...USER_STORAGE_KEYS]);

    const handleStorageChange = (event) => {
      if (!event.key || !relevantKeys.has(event.key)) {
        return;
      }
      refresh();
    };

    const handleAuthEvent = () => {
      refresh();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLoggedIn', handleAuthEvent);
    window.addEventListener('userSessionUpdated', handleAuthEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleAuthEvent);
      window.removeEventListener('userSessionUpdated', handleAuthEvent);
    };
  }, [refresh]);

  const contextValue = useMemo(
    () => ({
      token: authState.token,
      user: authState.user,
      permissions: authState.permissions,
      permissionIds: authState.permissionIds || [],
      isAuthenticated: authState.isAuthenticated,
      setSession,
      refresh,
      logout,
    }),
    [authState, logout, refresh, setSession],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

